<script setup lang="ts">
import {computed, inject, ref, shallowRef, watch, markRaw} from "vue";
import {AgGridVue} from "ag-grid-vue3";
import type {ColDef, ColGroupDef, GridReadyEvent, RowClassParams, ValueFormatterParams, ValueGetterParams} from "ag-grid-community";
import {GridState, StateUpdatedEvent} from "ag-grid-enterprise";
import {
  DashboardWidgetProps,
  dashboardPresenterKey,
  reportIsDashboardTypeKey,
  tableWidgetGridApiKey,
} from "@/utils/presenters/interfaces.ts";
import Translator from "@/utils/common/Translator.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import StorageUtils from "@/utils/common/StorageUtils.ts";
import BusinessFunction from "@/utils/constants/BusinessFunction.ts";
import RefreshIcon from "@/assets/editViewIcons/RefreshIcon.vue";
import ExportExcelIcon from "@/assets/listViewIcons/ExportExcelIcon.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import dayjs from "@/utils/dayjs";
import {format as formatNumber} from "numerable";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import {useDashboardViewOptionsStore} from "@/stores/dashboardViewOptionsStore.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {gridSurfaceColor, themeSafeBackground, themeSafeForeground} from "@/components/bi/themeSafeColors.ts";
import {useBIInteractionsForAgGrid} from "@/components/dashboardWidgets/useBIInteractionsForAgGrid.ts";
import BadgeCellRenderer from "@/components/dashboardWidgets/cellRenderers/BadgeCellRenderer.vue";
import IconCellRenderer from "@/components/dashboardWidgets/cellRenderers/IconCellRenderer.vue";
import HtmlCellRenderer from "@/components/dashboardWidgets/cellRenderers/HtmlCellRenderer.vue";
import SparklineCellRenderer from "@/components/dashboardWidgets/cellRenderers/SparklineCellRenderer.vue";
import EchartsBarCellRenderer from "@/components/dashboardWidgets/cellRenderers/EchartsBarCellRenderer.vue";
import CssBarCellRenderer from "@/components/dashboardWidgets/cellRenderers/CssBarCellRenderer.vue";
import type {
  BIDrillDownByClick,
  BIDrillDownClick,
  CrossFilterEmission,
  EnhancedTableCellData,
  EnhancedTableCellStyle,
  EnhancedTableColumnConfig,
  EnhancedTableColumnGroup,
  EnhancedTableCompactCell,
  EnhancedTableCompactRow,
  EnhancedTableData,
  EnhancedTableFormatting,
  EnhancedTableOptions,
  EnhancedTableRowData,
} from "@/components/bi/biChartTypes.ts";
import WizardSlotSelectorButton from "@/components/bi/WizardSlotSelectorButton.vue";
import BIWidgetRuntimeSelectorInfo = namaTypes.BIWidgetRuntimeSelectorInfo;

const props = defineProps<DashboardWidgetProps>();
const emit = defineEmits<{
  crossFilterClicked: [emissions: CrossFilterEmission[]];
  drillDownRequested: [click: BIDrillDownClick];
  drillDownByRequested: [click: BIDrillDownByClick];
}>();

const chartLine = ref(props.chartLine);
const dashboardPresenter = inject(dashboardPresenterKey, undefined);
const gridApi = inject(tableWidgetGridApiKey, shallowRef());
const reportIsDashboardType = inject(reportIsDashboardTypeKey, computed(() => false));
const runtimeSelectorInfo = computed<BIWidgetRuntimeSelectorInfo | undefined>(
  () => (chartLine.value as any)?.runtimeSelectorInfo);
const initialState = ref<GridState | undefined>(undefined);
const isRefreshClicked = ref(false);

watch(() => props.chartLine, (newLine) => {
  chartLine.value = newLine;
});

const enhancedTableData = computed<EnhancedTableData | undefined>(
  () => (chartLine.value as any)?.enhancedTableData as EnhancedTableData | undefined,
);
const columns = computed<EnhancedTableColumnConfig[]>(() => enhancedTableData.value?.columns ?? []);
const columnGroups = computed<EnhancedTableColumnGroup[]>(() => enhancedTableData.value?.columnGroups ?? []);
const tableOptions = computed<EnhancedTableOptions>(() => enhancedTableData.value?.tableOptions ?? {});
const stylesPalette = computed<EnhancedTableCellStyle[]>(() => enhancedTableData.value?.styles ?? []);

// The server emits a compact wire format (cells as positional array, style palette at root,
// short keys). Normalize it here into the {cells: {colId: ...}, _rowStyle} shape that the
// rest of the widget + cell renderers expect.
function normalizeCell(raw: EnhancedTableCompactCell, palette: EnhancedTableCellStyle[]): EnhancedTableCellData {
  if (raw === null || raw === undefined)
    return {value: null, displayValue: '', style: null};
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    const scalar = raw as string | number;
    return {value: scalar, displayValue: String(scalar), style: null};
  }
  const value = raw.v ?? null;
  const displayValue = raw.dv ?? (value == null ? '' : String(value));
  const style = typeof raw.s === 'number' ? (palette[raw.s] ?? null) : null;
  const sparklineData = raw.sd;
  const out: EnhancedTableCellData = {value, displayValue, style};
  if (sparklineData) out.sparklineData = sparklineData;
  return out;
}

const rowData = computed<EnhancedTableRowData[]>(() => {
  const data = enhancedTableData.value;
  if (!data?.rows?.length || !data.columns?.length) return [];
  const palette = data.styles ?? [];
  const cols = data.columns;
  return (data.rows as EnhancedTableCompactRow[]).map((row, rowIdx) => {
    const cells: Record<string, EnhancedTableCellData> = {};
    const cellArray = row.c ?? [];
    for (let i = 0; i < cols.length; i++)
      cells[cols[i].id] = normalizeCell(cellArray[i], palette);
    const rowStyle = typeof row.rs === 'number' ? (palette[row.rs] ?? null) : null;
    const out: EnhancedTableRowData = {cells, _rowStyle: rowStyle, _biRowIdx: rowIdx};
    if (typeof row.t === 'number') out._totalLevel = row.t;
    return out;
  });
});

// --- Cell renderer registry ---
// bar / progress / sparkline are rendered by dedicated Vue components below
// (see buildColDef); only the text-with-markup variants are looked up by key here.
const rendererRegistry = {
  badge: markRaw(BadgeCellRenderer),
  icon: markRaw(IconCellRenderer),
  html: markRaw(HtmlCellRenderer),
} as const;

// Per-column visual style for bar/progress renderers comes from `renderer.<type>.style`
// ('simple' | 'interactive'). Default = 'simple' (CSS bar). Sparklines always use the
// ECharts renderer; there is no style choice.
function resolveBarStyle(col: EnhancedTableColumnConfig): 'simple' | 'interactive' {
  return col.renderer?.bar?.style ?? 'simple';
}
function resolveProgressStyle(col: EnhancedTableColumnConfig): 'simple' | 'interactive' {
  return col.renderer?.progress?.style ?? 'simple';
}

function getColumnId(col: EnhancedTableColumnConfig): string {
  return col.id ?? col.field ?? 'col';
}

function getColumnHeaderName(col: EnhancedTableColumnConfig): string {
  return Translator.getName(col.headerArTitle, col.headerEnTitle) || getColumnId(col);
}

function getGroupHeaderName(group: EnhancedTableColumnGroup): string {
  return Translator.getName(group.headerArTitle, group.headerEnTitle) || group.headerName || group.id;
}

// Conditional-format colours are authored as absolute values, almost always picked while looking
// at the light theme, while the grid's own text and surface colours come from the active theme. An
// opaque pastel background therefore turns unreadable in dark mode (light text on a light fill), a
// dark fill is just as unreadable in light mode, and a text colour given on its own goes the same
// way. themeSafeColors keeps each authored colour exactly as written whenever it still reads
// against the active theme, and adapts it in the same hue when it does not - which for a
// background means tinting rather than filling, as totalDefaultStyle() below already does. A style
// that sets `bg` and `color` together is applied verbatim: the author owns that pair.
function toCellStyleCss(style: EnhancedTableCellStyle | null | undefined): Record<string, string | undefined> {
  if (!style) return {};
  return {
    backgroundColor: themeSafeBackground(style.bg, style.color),
    color: style.bg ? style.color : themeSafeForeground(style.color, gridSurfaceColor()),
    fontWeight: style.bold ? '700' : undefined,
    fontStyle: style.italic ? 'italic' : undefined,
    textDecoration: style.underline ? 'underline' : undefined,
    border: style.border,
    textAlign: style.align,
  };
}

// Default styling for cross-tab subtotal/grand-total rows and columns. Grand total (level 0) gets
// a stronger emphasis than nested subtotals (level >= 1). Explicit conditional formatting still
// wins because the explicit style is layered on top.
function totalDefaultStyle(level: number | undefined): Record<string, string | undefined> {
  if (level === undefined) return {};
  if (level === 0) return {fontWeight: '700', backgroundColor: 'rgba(33,150,243,0.10)'};
  return {fontWeight: '600', backgroundColor: 'rgba(33,150,243,0.05)'};
}

// AG Grid generates the grand-total footer row itself, so it carries no server-built cell and no
// displayValue. Format such values here from the column's own formatting block, mirroring the
// number/currency/percent branches of the server-side ValueFormatter, so the total row reads the
// same as the data rows above it. Server-side pivot totals are ordinary data rows with cells and
// never reach this code.
function decimalPatternFor(formatting: EnhancedTableFormatting): string {
  const decimals = formatting.decimals ?? 2;
  const integerPart = formatting.thousandSeparator === false ? '0' : '0,0';
  return decimals > 0 ? integerPart + '.' + '0'.repeat(decimals) : integerPart;
}

function formatWithColumnFormatting(value: unknown, formatting: EnhancedTableFormatting | undefined): string {
  const raw = String(value);
  if (!formatting) return raw;
  const type = formatting.type ?? 'text';
  if (type !== 'number' && type !== 'currency' && type !== 'percent') return raw;
  let numeric = Number(raw);
  if (!Number.isFinite(numeric)) return raw;
  if (type === 'percent' && formatting.percentScale === 'fraction') numeric = numeric * 100;
  let formatted = formatNumber(numeric, decimalPatternFor(formatting));
  if (type === 'percent') {
    formatted = formatted + '%';
  } else if (type === 'currency') {
    const symbol = formatting.currencySymbol || formatting.currencyCode || '';
    if (symbol)
      formatted = formatting.currencyPlacement === 'prefix' ? symbol + ' ' + formatted : formatted + ' ' + symbol;
  }
  return (formatting.prefix ?? '') + formatted + (formatting.suffix ?? '');
}

function readCell(data: EnhancedTableRowData | undefined, colId: string): EnhancedTableCellData | undefined {
  return data?.cells?.[colId];
}

function buildColDef(col: EnhancedTableColumnConfig): ColDef {
  const colId = getColumnId(col);
  const rendererType = col.renderer?.type ?? 'text';
  const baseCellClass = (params: { colDef: { colId?: string; field?: string }; data?: unknown }) => cellClassForInteraction(params);

  const colDef: ColDef = {
    colId,
    field: colId,
    headerName: getColumnHeaderName(col),
    valueGetter: (p: ValueGetterParams) => {
      if (rendererType === 'sparkline') return readCell(p.data, colId)?.sparklineData;
      return readCell(p.data, colId)?.value;
    },
    valueFormatter: (p: ValueFormatterParams) => {
      const cell = readCell(p.data, colId);
      if (cell?.displayValue != null) return cell.displayValue;
      const v = cell?.value ?? p.value;
      if (v == null) return '';
      if (!cell) return formatWithColumnFormatting(v, col.formatting);
      return String(v);
    },
    cellStyle: (p) => {
      const colTotal = totalDefaultStyle(col.t);
      const rowTotal = totalDefaultStyle((p.data as EnhancedTableRowData | undefined)?._totalLevel);
      const explicit = toCellStyleCss(readCell(p.data, colId)?.style);
      return {...colTotal, ...rowTotal, ...explicit};
    },
    cellClass: baseCellClass as any,
    hide: col.hide === true,
    width: col.width,
    minWidth: col.minWidth,
    maxWidth: col.maxWidth,
    flex: col.flex ?? undefined,
    pinned: resolvePinned(col.pinned),
    sort: col.sort ?? undefined,
    sortIndex: col.sortIndex ?? undefined,
    rowGroup: col.rowGroup === true,
    rowGroupIndex: col.rowGroupIndex ?? undefined,
    pivot: col.pivot === true,
    aggFunc: (col.aggFunc as any) ?? undefined,
  };
  if (col.tooltipField) {
    colDef.tooltipValueGetter = (p) => readCell(p.data, col.tooltipField!)?.displayValue ?? String(readCell(p.data, col.tooltipField!)?.value ?? '');
  }
  if (rendererType === 'sparkline') {
    colDef.cellRenderer = markRaw(SparklineCellRenderer);
    colDef.cellRendererParams = {rendererOptions: col.renderer, colId};
  } else if (rendererType === 'bar') {
    const useInteractive = resolveBarStyle(col) === 'interactive';
    colDef.cellRenderer = markRaw(useInteractive ? EchartsBarCellRenderer : CssBarCellRenderer);
    colDef.cellRendererParams = {rendererOptions: col.renderer, colId, mode: 'bar'};
  } else if (rendererType === 'progress') {
    const useInteractive = resolveProgressStyle(col) === 'interactive';
    colDef.cellRenderer = markRaw(useInteractive ? EchartsBarCellRenderer : CssBarCellRenderer);
    colDef.cellRendererParams = {rendererOptions: col.renderer, colId, mode: 'progress'};
  } else if (rendererType !== 'text' && rendererRegistry[rendererType as keyof typeof rendererRegistry]) {
    colDef.cellRenderer = rendererRegistry[rendererType as keyof typeof rendererRegistry];
    colDef.cellRendererParams = {rendererOptions: col.renderer, colId};
  }
  return colDef;
}

const columnDefs = computed<(ColDef | ColGroupDef)[]>(() => {
  if (!columns.value.length) return [];
  const groupsById = new Map<string, ColGroupDef>();
  const parentByGroupId = new Map<string, string | undefined>();
  for (const g of columnGroups.value) {
    groupsById.set(g.id, {
      headerName: getGroupHeaderName(g),
      groupId: g.id,
      marryChildren: g.marryChildren ?? false,
      openByDefault: g.openByDefault ?? true,
      children: [],
    });
    parentByGroupId.set(g.id, g.parentGroupId);
  }
  const topLevel: (ColDef | ColGroupDef)[] = [];
  const attachedGroupIds = new Set<string>();
  function attachGroup(groupId: string) {
    if (attachedGroupIds.has(groupId)) return;
    attachedGroupIds.add(groupId);
    const parentId = parentByGroupId.get(groupId);
    const group = groupsById.get(groupId)!;
    if (parentId && groupsById.has(parentId)) {
      attachGroup(parentId);
      (groupsById.get(parentId)!.children as (ColDef | ColGroupDef)[]).push(group);
    } else {
      topLevel.push(group);
    }
  }
  for (const col of columns.value) {
    const colDef = buildColDef(col);
    if (col.groupId && groupsById.has(col.groupId)) {
      attachGroup(col.groupId);
      (groupsById.get(col.groupId)!.children as (ColDef | ColGroupDef)[]).push(colDef);
    } else {
      topLevel.push(colDef);
    }
  }
  return topLevel;
});

const defaultColDef = computed<ColDef>(() => ({
  filter: true,
  wrapText: tableOptions.value.wrapText !== false,
  autoHeight: tableOptions.value.autoHeight !== false,
  flex: tableOptions.value.defaultFlex ?? 1,
  minWidth: tableOptions.value.defaultMinWidth ?? 130,
  width: tableOptions.value.defaultColWidth ?? undefined,
  enableValue: true,
  enableRowGroup: tableOptions.value.enableRowGroup !== false,
  enablePivot: tableOptions.value.enablePivot === true,
  filterParams: {
    applyMiniFilterWhileTyping: true,
    textFormatter: (value: string): string | null => value == null ? null : StringUtils.replaceArNumerals(value),
    buttons: ["reset"],
  },
}));

// Resolve RTL from tableOptions.enableRtl (explicit author choice) or fall back to the active
// locale. AG Grid's enableRtl is initial-only, so the value is captured once at mount.
function resolveEnableRtl(): boolean {
  const explicit = tableOptions.value.enableRtl;
  if (explicit === true) return true;
  if (explicit === false) return false;
  return Translator.isLanguageRTL();
}

// Translate logical pinning (start/end) to AG Grid's physical pinning (left/right) based on the
// active RTL direction. Pass null/undefined through unchanged.
function resolvePinned(pinned: 'start' | 'end' | null | undefined): 'left' | 'right' | undefined {
  if (pinned !== 'start' && pinned !== 'end') return undefined;
  const rtl = resolveEnableRtl();
  return pinned === 'start' ? (rtl ? 'right' : 'left') : (rtl ? 'left' : 'right');
}

function getRowStyle(params: RowClassParams) {
  const row = params.data as EnhancedTableRowData | undefined;
  const total = totalDefaultStyle(row?._totalLevel);
  const explicit = toCellStyleCss(row?._rowStyle);
  return {...total, ...explicit} as any;
}

// --- BI interaction ---
const widgetId = computed<string | undefined>(() => chartLine.value.widgetId);
const {
  isFilterSource,
  isFilteredByOther,
  filteredByTooltip,
  emittingTooltip,
  onCellClicked,
  getContextMenuItems,
  cellClassForInteraction,
} = useBIInteractionsForAgGrid({
  chartLine,
  widgetId,
  dashboardId: () => props.dashboardId,
  emit: {
    crossFilterClicked: (emissions) => emit('crossFilterClicked', emissions),
    drillDownRequested: (click) => emit('drillDownRequested', click),
    drillDownByRequested: (click) => emit('drillDownByRequested', click),
  },
});

function onGridReady(params: GridReadyEvent) {
  gridApi.value = params.api;
  params.api.setGridOption('getContextMenuItems', getContextMenuItems);
  if (!initialState.value)
    gridApi.value.autoSizeAllColumns(false);
  applyGrandTotalRow();
  applyGroupFooter();
}

const onStateUpdated = (event: StateUpdatedEvent) => {
  NamaCommon.setInStorage(StorageUtils.calcDashboardTableStatePath(chartLine.value), event.state);
};

function applyGrandTotalRow() {
  const mode = tableOptions.value.grandTotalRow;
  if (mode && gridApi.value)
    gridApi.value.setGridOption('grandTotalRow', mode);
}

function applyGroupFooter() {
  const enabled = tableOptions.value.groupIncludeFooter === true;
  if (gridApi.value)
    gridApi.value.setGridOption('groupIncludeFooter', enabled);
}

watch(tableOptions, () => {
  applyGrandTotalRow();
  applyGroupFooter();
});

// Conditional-format backgrounds resolve against the active theme (toThemeSafeBackground), so the
// rows have to be repainted when the user flips dark mode.
watch(() => useNamaFlagsStore().darkMode, () => gridApi.value?.redrawRows());

async function onRefreshClicked() {
  if (isRefreshClicked.value) return;
  isRefreshClicked.value = true;
  try {
    if (dashboardPresenter) {
      await dashboardPresenter.redrawWidgets(chartLine.value.widgetId);
      return;
    }
    await legacyManualRefresh();
  } finally {
    isRefreshClicked.value = false;
  }
}

async function legacyManualRefresh() {
  const response = await NamaCommon.namaFetch(NamaURLS.FetchCharts, {
    params: {dashBoardId: props.dashboardId, widgetId: chartLine.value.widgetId},
    data: [],
    noLoadingIndicator: true,
  });
  if (!NamaCommon.isSuccess(response) || !response.data?.chartLines?.length) return;
  const myChartLine = response.data.chartLines.find((l: any) => l.widgetId === chartLine.value.widgetId);
  if (myChartLine) chartLine.value = myChartLine;
}

function onGroupExpand() {
  let isRowExpanded: boolean | null = null;
  const rowNodes = gridApi.value?.getRenderedNodes();
  if (rowNodes) {
    for (const rowNode of rowNodes) {
      if (!rowNode.group) continue;
      isRowExpanded = !!rowNode.expanded;
      break;
    }
  }
  if (isRowExpanded)
    gridApi.value?.collapseAll();
  else
    gridApi.value?.expandAll();
}

function onExportToExcel() {
  const name = (props.chartLine.enChartTitle ?? props.chartLine.arChartTitle ?? "EnhancedTable") + "." + dayjs().format("yyyyMMDD.HHmm");
  gridApi.value?.exportDataAsExcel({fileName: name + ".xlsx"});
}

const viewOptionsStore = useDashboardViewOptionsStore();
const isAdmin = computed(() => LoginUtils.isCurrentUserAdminOrTreatAsAdmin());
const showAdminToolbox = computed(() => isAdmin.value && viewOptionsStore.showToolbox);
const copyDataCopied = ref(false);

function copyEnhancedTableData() {
  const data = enhancedTableData.value;
  if (!data) return;
  const text = JSON.stringify(data, null, 2);
  navigator.clipboard.writeText(text).then(() => {
    copyDataCopied.value = true;
    setTimeout(() => copyDataCopied.value = false, 2000);
  });
}

function openWidgetEditView() {
  const id = widgetId.value;
  if (!id) return;
  const link = PlaceUtils.editLink('DashBoardWidget', id);
  if (link) window.open(link, '_blank');
}
</script>

<template>
  <div class="tw-w-full tw-h-full tw-flex tw-flex-col tw-items-center tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark tw-rounded tw-relative"
       :class="{'bi-widget-source': isFilterSource, 'bi-widget-filtered': isFilteredByOther}">
    <div v-if="isFilterSource || isFilteredByOther"
         class="tw-absolute tw-top-1 tw-end-1 tw-z-10 tw-flex tw-items-center tw-gap-1">
      <div v-if="isFilterSource"
           class="tw-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-primary tw-text-white tw-shadow"
           :title="emittingTooltip">
        <q-icon name="filter_alt" size="14px" class="emit-funnel"/>
      </div>
      <div v-if="isFilteredByOther"
           class="tw-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-info tw-text-white tw-shadow"
           :title="filteredByTooltip">
        <q-icon name="filter_alt" size="14px"/>
      </div>
    </div>
    <div class="tw-flex tw-items-center tw-gap-1 tw-my-1 dark:tw-text-white">
      <template v-if="!reportIsDashboardType">
        <a class="tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer"
           :class="{'nama-toolbar-disabled': isRefreshClicked}"
           @click.stop="onRefreshClicked"
           :title="Translator.translateId(BusinessFunction.refresh, 'BusinessFunction')">
          <RefreshIcon class="tw-h-5"
                       :class="{'!tw-fill-gray-300 dark:!tw-fill-gray-600': isRefreshClicked}"/>
        </a>
        <a class="tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer"
           :class="{'nama-toolbar-disabled': isRefreshClicked}"
           @click.stop="onExportToExcel"
           :title="Translator.translateId(BusinessFunction.exportAsXLSX)">
          <ExportExcelIcon class="tw-h-5"
                           :class="{'!tw-fill-gray-300 dark:!tw-fill-gray-600': isRefreshClicked}"/>
        </a>
      </template>
      <a class="tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer"
         @click.stop="onGroupExpand">
        <NamaIcon default-icon="nm-expand"/>
      </a>
      <a v-if="showAdminToolbox"
         class="tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer tw-flex tw-items-center tw-gap-1"
         :title="'Copy enhancedTableData JSON (admin)'"
         @click.stop="copyEnhancedTableData">
        <q-icon :name="copyDataCopied ? 'check' : 'content_copy'" size="18px"/>
        <span v-if="copyDataCopied" class="tw-text-xs">Copied</span>
      </a>
      <a v-if="showAdminToolbox"
         class="tw-bg-gray-200 active:tw-bg-gray-300 dark:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-lg tw-cursor-pointer"
         :title="'Open widget edit screen (admin)'"
         @click.stop="openWidgetEditView">
        <q-icon name="open_in_new" size="18px"/>
      </a>
      <WizardSlotSelectorButton v-if="runtimeSelectorInfo && widgetId"
                                :widget-id="widgetId"
                                :info="runtimeSelectorInfo"
                                @applied="async () => { if (widgetId) await dashboardPresenter?.redrawWidgets(widgetId); }"
                                @reset="async () => { if (widgetId) await dashboardPresenter?.redrawWidgets(widgetId); }"/>
      <span class="tw-line-clamp-1 tw-font-bold tw-text-base"
            :title="Translator.getName(chartLine.arChartTitle, chartLine.enChartTitle)"
            v-text="Translator.getName(chartLine.arChartTitle, chartLine.enChartTitle)"/>
    </div>
    <AgGridVue v-if="columns.length"
               v-bind="{...AGGridUtils.getTableDashboardSideBarConfig()}"
               style="width: 100%; height: 100%; font-size: var(--ag-font-size);"
               class="listview-grid"
               groupDisplayType="multipleColumns"
               rowModelType="clientSide"
               :pagination="tableOptions.pagination === true"
               :paginationPageSize="tableOptions.pageSize ?? 25"
               :paginationPageSizeSelector="tableOptions.pageSizes ?? [25, 50, 100, 250, 1000, 2500, 5000]"
               :groupDefaultExpanded="tableOptions.groupDefaultExpanded ?? 0"
               :rowNumbers="tableOptions.rowNumbers !== false"
               :cellSelection="tableOptions.cellSelection !== false"
               :columnDefs="columnDefs"
               :defaultColDef="defaultColDef"
               :rowData="rowData"
               :showOpenedGroup="true"
               :suppressHeaderFocus="true"
               :enableRtl="resolveEnableRtl()"
               :localeText="Translator.fetchAGGridLocale()"
               :getRowStyle="getRowStyle"
               @cell-clicked="onCellClicked"
               @grid-ready="onGridReady"
               @state-updated="onStateUpdated"
               :enableCellTextSelection="true"
               :processCellForClipboard="AGGridUtils.processCellForClipboard"/>
  </div>
</template>

<style scoped>
.bi-widget-source {
  box-shadow: inset 0 0 0 2px var(--q-primary);
}
.bi-widget-filtered {
  box-shadow: inset 0 0 0 1px var(--q-info);
}
.bi-widget-source.bi-widget-filtered {
  box-shadow: inset 0 0 0 2px var(--q-primary), inset 0 0 0 4px var(--q-info);
}
.emit-funnel {
  transform: rotate(180deg);
}
</style>

<style>
.listview-grid .bi-clickable-cell {
  cursor: pointer;
}
.listview-grid .bi-clickable-cell:hover {
  text-decoration: underline;
}
</style>
